import { useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button } from '@astryxdesign/core/Button';
import { HStack } from '@astryxdesign/core/HStack';
import { List, ListItem } from '@astryxdesign/core/List';
import { Section } from '@astryxdesign/core/Section';
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl';
import { Heading, Text } from '@astryxdesign/core/Text';
import { VStack } from '@astryxdesign/core/VStack';
import { EmptyView } from '@/components/StateViews';
import { useTenant } from '@/features/tenant/context';
import { priceFor, priceRange } from '@/features/booking/pricing';
import { VehicleClassPicker } from '@/features/booking/VehicleClassPicker';
import { formatDuration, formatMoney, formatPriceRange } from '@/lib/format';
import { readJson, writeJson } from '@/lib/storage';

type Tab = 'main' | 'addon' | 'info';

export function ServicesPage() {
  const t = useTenant();
  const [params, setParams] = useSearchParams();
  const tab = (params.get('tab') as Tab | null) ?? 'main';
  const [classId, setClassId] = useState<string | null>(() => readJson<string | null>(`vehicle-class:${t.slug}`, null));
  const list = t.services.filter((s) => s.category === tab);
  const hasInfo = t.services.some((s) => s.category === 'info');
  const base = `/s/${t.slug}`;

  const pickClass = (id: string) => {
    setClassId(id);
    writeJson(`vehicle-class:${t.slug}`, id);
  };

  return (
    <VStack as="main" gap={4} paddingInline={4} paddingBlockStart={4} className="app-page" maxWidth={720} style={{ marginInline: 'auto' }}>
      <VStack gap={1}>
        <Heading level={1}>Услуги</Heading>
        <Text color="secondary">{t.services.length} услуг · запись без регистрации</Text>
      </VStack>
      <VehicleClassPicker tenant={t} value={classId} onChange={pickClass} label="Показать цены для" />
      <SegmentedControl label="Категория" value={tab} onChange={(v) => setParams({ tab: v }, { replace: true })} layout="fill">
        <SegmentedControlItem value="main" label="Основные" />
        <SegmentedControlItem value="addon" label="Доп. услуги" />
        {hasInfo ? <SegmentedControlItem value="info" label="Прочее" /> : null}
      </SegmentedControl>

      {list.length === 0 ? (
        <EmptyView title="Здесь пока пусто" description="Студия ещё не добавила услуги в эту категорию." />
      ) : (
        <Section padding={1} className="app-panel">
        <List hasDividers>
          {list.map((s) => {
            const exact = priceFor(s, classId);
            const price = s.price_note ?? (exact !== null ? formatMoney(exact, t.currency, t.locale) : formatPriceRange(priceRange(s), t.currency, t.locale));
            return (
              <ListItem
                key={s.id}
                label={s.name}
                description={
                  <VStack gap={1}>
                    {s.description ? <Text color="secondary" size="sm">{s.description}</Text> : null}
                    <Text size="sm" color="secondary">{formatDuration(s.duration_minutes)}{s.spans_closed_hours ? ' · машина остаётся в студии' : ''}</Text>
                  </VStack>
                }
                endContent={
                  <VStack align="end" gap={1.5}>
                    <Text weight="semibold" color="accent" hasTabularNumbers>{price}</Text>
                    {s.bookable ? (
                      <Button label="Выбрать" size="sm" variant={s.category === 'main' ? 'primary' : 'secondary'} href={`${base}/booking?service=${s.id}`} />
                    ) : (
                      <Text size="sm" color="secondary">по телефону</Text>
                    )}
                  </VStack>
                }
              />
            );
          })}
        </List>
        </Section>
      )}
      {t.phone ? (
        <HStack justify="center">
          <Button label="Позвонить в студию" variant="ghost" href={`tel:${t.phone}`} />
        </HStack>
      ) : null}
    </VStack>
  );
}
